import { Link } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { BackIcon } from "../../components/icons";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  type ArenaModel,
  type ArenaResult,
  useArenaModels,
  useArenaSummary,
  useArenaTurn,
  useArenaVote,
} from "../../lib/queries";

const FLAGS = [
  ["invento", "Inventó"],
  ["precio", "Dio precio"],
  ["no_asesor", "No pasó al asesor"],
  ["larga", "Muy larga"],
  ["otro", "Otro problema"],
] as const;
type Flag = (typeof FLAGS)[number][0];

interface Turn {
  turnId: string;
  message: string;
  results: ArenaResult[];
  best: string | null | undefined;
  flags: Record<string, Flag[]>;
}

const STORAGE = "orbis.arena.models";

const FIELD_LABEL: Record<string, string> = {
  name: "nombre",
  province: "provincia",
  canton: "cantón",
  type: "tipo",
  email: "correo",
};
const usd = (v: number) =>
  `USD ${v.toLocaleString("es-CR", { minimumFractionDigits: v < 0.01 ? 4 : 2, maximumFractionDigits: v < 0.01 ? 4 : 2 })}`;
const secs = (ms: number) => `${(ms / 1000).toLocaleString("es-CR", { maximumFractionDigits: 1 })} s`;

function loadSelection(defaults: string[], available: ArenaModel[]): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE) ?? "null") as string[] | null;
    const valid = saved?.filter((id) => available.some((m) => m.id === id));
    if (valid && valid.length >= 2) return valid.slice(0, 4);
  } catch {
    // No storage (private window): use the defaults.
  }
  return defaults;
}

/**
 * The owner plays the customer; several models answer the same message side by side
 * with letters instead of names, and the owner picks the best reply, which continues
 * the conversation. Nothing reaches WhatsApp.
 */
export function ArenaPage() {
  const models = useArenaModels();
  if (models.isPending) return <div className="page">Cargando…</div>;
  if (!models.data)
    return (
      <div className="page">
        <Link className="back-link" to="/lumi">
          <BackIcon />
          Lumi
        </Link>
        <p>La arena de IA solo está disponible para el dueño, en el entorno de pruebas.</p>
      </div>
    );
  return <Arena available={models.data.items} defaults={models.data.defaults} />;
}

function Arena({ available, defaults }: { available: ArenaModel[]; defaults: string[] }) {
  const [selected, setSelected] = useState(() => loadSelection(defaults, available));
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [turns, setTurns] = useState<Turn[]>([]);
  const [reveal, setReveal] = useState(false);
  const [draft, setDraft] = useState("");
  const [ended, setEnded] = useState(false);
  /** What the pretend customer has told the chosen replies so far. */
  const [contact, setContact] = useState<Record<string, string>>({});
  const runTurn = useArenaTurn();
  const vote = useArenaVote();
  const summary = useArenaSummary();
  const toast = useToast();
  const bottom = useRef<HTMLDivElement>(null);
  const label = (id: string) => available.find((m) => m.id === id)?.label ?? id;
  const last = turns.at(-1);
  const waitingVote = !!last && last.best === undefined;
  const sessionCost = turns.reduce((s, t) => s + t.results.reduce((a, r) => a + r.costUsd, 0), 0);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE, JSON.stringify(selected));
    } catch {
      // Storage unavailable: the choice lasts for this visit only.
    }
  }, [selected]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scrolls when a turn starts or arrives
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, runTurn.isPending]);

  /** What every model sees: the customer's messages and the chosen replies. */
  const history = useMemo(
    () =>
      turns.flatMap((t) => {
        const chosen = t.results.find((r) => r.model === t.best);
        return chosen
          ? [
              { role: "user" as const, content: t.message },
              { role: "assistant" as const, content: chosen.reply },
            ]
          : [];
      }),
    [turns],
  );

  function toggleModel(id: string) {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length >= 4 ? s : [...s, id]));
  }

  function restart() {
    setTurns([]);
    setContact({});
    setEnded(false);
    setDraft("");
    setSessionId(crypto.randomUUID());
  }

  async function onSend(e: FormEvent) {
    e.preventDefault();
    const message = draft.trim();
    if (!message || waitingVote || ended) return;
    try {
      const r = await runTurn.mutateAsync({ sessionId, history, message, models: selected, contact });
      setTurns((ts) => [...ts, { turnId: r.turnId, message, results: r.results, best: undefined, flags: {} }]);
      setDraft("");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo enviar.");
    }
  }

  function save(turn: Turn, patch: Partial<Pick<Turn, "best" | "flags">>) {
    const next = { ...turn, ...patch };
    // The chosen reply continues the conversation, so what it saved is now known.
    const chosen = patch.best ? turn.results.find((r) => r.model === patch.best) : undefined;
    if (chosen?.saved) setContact((c) => ({ ...c, ...chosen.saved }));
    setTurns((ts) => ts.map((t) => (t.turnId === turn.turnId ? next : t)));
    if (next.best === undefined) return;
    vote.mutate(
      { turnId: turn.turnId, best: next.best, flags: next.flags },
      { onError: () => toast("No se guardó el voto. Intente de nuevo.") },
    );
  }

  function toggleFlag(turn: Turn, model: string, flag: Flag) {
    const current = turn.flags[model] ?? [];
    const flags = {
      ...turn.flags,
      [model]: current.includes(flag) ? current.filter((f) => f !== flag) : [...current, flag],
    };
    // Before a choice, flags wait in the page and go with the vote.
    if (turn.best === undefined) setTurns((ts) => ts.map((t) => (t.turnId === turn.turnId ? { ...t, flags } : t)));
    else save(turn, { flags });
  }

  return (
    <div className="page arena">
      <Link className="back-link" to="/lumi">
        <BackIcon />
        Lumi
      </Link>
      <div className="page-head">
        <h1>Arena de IA</h1>
        <div className="products-actions">
          <button className="btn" type="button" aria-pressed={reveal} onClick={() => setReveal((v) => !v)}>
            {reveal ? "Ocultar modelos" : "Mostrar modelos"}
          </button>
          <button className="btn" type="button" onClick={restart}>
            Nueva conversación
          </button>
        </div>
      </div>
      <p className="muted kb-intro">
        Escriba como lo haría un cliente por WhatsApp. Cada modelo responde con los datos de la empresa y sin saber cuál
        es cuál; elija la mejor respuesta para seguir la conversación. Nada se envía por WhatsApp.
      </p>

      <details className="disclosure arena-models">
        <summary>
          Modelos en la mesa ({selected.length} de 4) · gastado en esta conversación {usd(sessionCost)}
          {summary.data ? ` · total ${usd(summary.data.spentUsd)}` : ""}
        </summary>
        <div className="disclosure-body">
          <p className="faint">Elija de 2 a 4. Los marcados "caro" gastan unas 10 veces más del saldo de pruebas.</p>
          <div className="arena-picker">
            {available.map((m) => (
              <label key={m.id} className="check arena-pick">
                <input
                  type="checkbox"
                  checked={selected.includes(m.id)}
                  disabled={turns.length > 0 || (!selected.includes(m.id) && selected.length >= 4)}
                  onChange={() => toggleModel(m.id)}
                />
                <span>{m.label}</span>
                <span className="faint">{m.origin}</span>
                {m.tier !== "barato" && <span className="pill pill-warn">{m.tier}</span>}
              </label>
            ))}
          </div>
          {turns.length > 0 && <p className="faint">Para cambiar los modelos, empiece una conversación nueva.</p>}
        </div>
      </details>

      {Object.keys(contact).length > 0 && (
        <p className="faint arena-contact">
          Ficha del cliente de prueba:{" "}
          {Object.entries(contact)
            .map(([k, v]) => `${FIELD_LABEL[k] ?? k}: ${v}`)
            .join(" · ")}
        </p>
      )}

      <div className="arena-log">
        {turns.map((t, i) => (
          <section key={t.turnId} className="arena-turn" aria-label={`Mensaje ${i + 1}`}>
            <p className="arena-customer">{t.message}</p>
            <div className="arena-results">
              {t.results.map((r) => {
                const chosen = t.best === r.model;
                const flags = t.flags[r.model] ?? [];
                return (
                  <article key={r.model} className={`card arena-result${chosen ? " chosen" : ""}`}>
                    <header>
                      <strong>{reveal ? label(r.model) : `Modelo ${r.slot}`}</strong>
                      <span className="faint">
                        {secs(r.latencyMs)} · {usd(r.costUsd)}
                      </span>
                    </header>
                    {r.error ? (
                      <p className="banner banner-warn">{r.error}</p>
                    ) : (
                      <p className="arena-reply">{r.reply || "(respuesta vacía)"}</p>
                    )}
                    <div className="arena-meta">
                      {r.handoff && <span className="pill pill-human">Pasó a un asesor</span>}
                      {r.tools.length > 0 && (
                        <span className="faint">Consultó: {[...new Set(r.tools)].join(", ")}</span>
                      )}
                      {r.saved && Object.keys(r.saved).length > 0 && (
                        <span className="pill pill-bot">
                          Guardó:{" "}
                          {Object.entries(r.saved)
                            .map(([k, v]) => `${FIELD_LABEL[k] ?? k} ${v}`)
                            .join(", ")}
                        </span>
                      )}
                    </div>
                    <fieldset className="arena-flags">
                      <legend className="sr-only">Marcar un problema</legend>
                      {FLAGS.map(([key, text]) => (
                        <button
                          key={key}
                          type="button"
                          className="arena-flag"
                          aria-pressed={flags.includes(key)}
                          onClick={() => toggleFlag(t, r.model, key)}
                        >
                          {text}
                        </button>
                      ))}
                    </fieldset>
                    {!r.error && (
                      <button
                        className={`btn btn-sm${chosen ? " btn-primary" : ""}`}
                        type="button"
                        disabled={t !== last || ended}
                        onClick={() => save(t, { best: r.model })}
                      >
                        {chosen ? "Elegida" : "Elegir esta"}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
            {t === last && t.best === undefined && (
              <button
                className="btn btn-sm btn-ghost"
                type="button"
                onClick={() => {
                  save(t, { best: null });
                  setEnded(true);
                }}
              >
                Ninguna sirve (termina la conversación)
              </button>
            )}
          </section>
        ))}
        {runTurn.isPending && <p className="muted arena-waiting">Los modelos están respondiendo…</p>}
        <div ref={bottom} />
      </div>

      <form className="arena-composer" onSubmit={onSend}>
        <textarea
          className="textarea"
          aria-label="Mensaje del cliente"
          rows={2}
          maxLength={2000}
          placeholder={
            ended
              ? "Conversación terminada: empiece una nueva."
              : waitingVote
                ? "Elija la mejor respuesta para seguir."
                : "Hola, ocupo una plantilla de gas para empotrar…"
          }
          disabled={ended || waitingVote || runTurn.isPending}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <button
          className="btn btn-primary"
          type="submit"
          disabled={!draft.trim() || ended || waitingVote || runTurn.isPending || selected.length < 2}
        >
          Enviar
        </button>
      </form>

      <details className="disclosure arena-summary">
        <summary>Resultados acumulados</summary>
        <div className="disclosure-body">
          {summary.data && summary.data.models.length > 0 ? (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Modelo</th>
                    <th className="num">Respondió</th>
                    <th className="num">Elegido</th>
                    <th className="num">Con problemas</th>
                    <th className="num">Errores</th>
                    <th className="num">Costo promedio</th>
                    <th className="num">Tiempo promedio</th>
                  </tr>
                </thead>
                <tbody>
                  {[...summary.data.models]
                    .sort((a, b) => b.winRate - a.winRate)
                    .map((m) => (
                      <tr key={m.model}>
                        <td>{label(m.model)}</td>
                        <td className="num">{m.answered}</td>
                        <td className="num">
                          {m.wins} de {m.voted}
                        </td>
                        <td className="num">{m.flagged}</td>
                        <td className="num">{m.errors}</td>
                        <td className="num">{usd(m.avgCostUsd)}</td>
                        <td className="num">{secs(m.avgLatencyMs)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">Todavía no hay votos.</p>
          )}
          <p className="faint">
            {summary.data ? `${summary.data.sessions} conversaciones, ${summary.data.turns} mensajes.` : ""}
          </p>
        </div>
      </details>
    </div>
  );
}
